<template>
  <div class="max-w-7xl mx-auto pb-10 relative space-y-6">

    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal }">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <CreditCard class="w-6 h-6 text-indigo-500" />
          Planes y Límites
        </h1>
        <p class="text-slate-500 text-sm mt-1">
          Configura las membresías de Neurals y restringe el uso del Wizard.
        </p>
      </div>
      <button
        @click="openCreate"
        class="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl shadow-lg transition-all active:scale-95"
      >
        <Plus class="w-5 h-5" /> Crear Plan
      </button>
    </div>

    <!-- Carga -->
    <div v-if="isLoading" class="flex justify-center py-20">
      <Loader2 class="w-8 h-8 text-indigo-500 animate-spin" />
    </div>

    <!-- Grid de Planes -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal }">

      <div
        v-for="plan in plans"
        :key="plan.id"
        class="bg-white rounded-3xl border transition-all flex flex-col relative"
        :class="plan.is_active == 1 ? 'border-slate-200 shadow-xl shadow-slate-200/50 hover:border-indigo-300' : 'border-slate-200 bg-slate-50 opacity-75'"
      >
        <div v-if="plan.is_active == 0" class="absolute top-4 right-4 bg-slate-200 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Inactivo</div>

        <div class="p-8 border-b border-slate-100 flex-1">
          <h3 class="text-xl font-black text-slate-900">{{ plan.name }}</h3>
          <p class="text-sm text-slate-500 mt-1 min-h-[40px]">{{ plan.description }}</p>

          <div class="my-6 flex items-baseline gap-1">
            <span class="text-4xl font-black text-slate-900">${{ plan.price }}</span>
            <span class="text-slate-500 font-medium">/mes</span>
          </div>

          <!-- Límites del Wizard -->
          <ul class="space-y-3 text-sm text-slate-600 font-medium mt-6">
            <li class="flex items-center gap-3">
              <Smartphone class="w-4 h-4 text-emerald-500" />
              <span>Hasta <strong class="text-slate-900">{{ plan.max_instances }}</strong> Instancias</span>
            </li>
            <li class="flex items-center gap-3">
              <ShoppingBag class="w-4 h-4 text-indigo-500" />
              <span><strong class="text-slate-900">{{ plan.max_products }}</strong> Productos / Catálogo</span>
            </li>
            <li class="flex items-center gap-3">
              <ShieldAlert class="w-4 h-4 text-purple-500" />
              <span><strong class="text-slate-900">{{ plan.max_rules }}</strong> Reglas de Negocio</span>
            </li>
            <li class="flex items-center gap-3">
              <MessageSquare class="w-4 h-4 text-sky-500" />
              <span><strong class="text-slate-900">{{ plan.max_words_response }}</strong> Palabras máx / respuesta</span>
            </li>
          </ul>
        </div>

        <div class="p-4 bg-slate-50 rounded-b-3xl flex gap-2">
          <button @click="openEdit(plan)" class="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 font-bold text-sm rounded-xl hover:bg-slate-50 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2">
            <Settings2 class="w-4 h-4" /> Editar
          </button>
          <button @click="deletePlan(plan)" class="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-100">
            <Trash2 class="w-5 h-5" />
          </button>
        </div>
      </div>

    </div>

    <!-- Formulario Modal -->
    <PlanForm
      :show="showModal"
      :planToEdit="selectedPlan"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import {
  CreditCard, Plus, Loader2, Smartphone,
  ShoppingBag, ShieldAlert, MessageSquare, Settings2, Trash2
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import PlanForm from './Form.vue';

const plans = ref([]);
const isLoading = ref(true);
const showModal = ref(false);
const selectedPlan = ref(null);
const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/plans');
    plans.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error cargando los planes');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const openCreate = () => { selectedPlan.value = null; showModal.value = true; };
const openEdit = (plan) => { selectedPlan.value = plan; showModal.value = true; };

const deletePlan = (plan) => {
  Swal.fire({
    title: '¿Eliminar Plan?',
    text: "Si eliminas este plan, los clientes actuales podrían perder sus límites referenciados.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/plans/${plan.id}`);
        showSuccess('Plan eliminado');
        loadData();
      } catch (e) {
        showError('No se pudo eliminar el plan');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
</style>